import { useLocation } from "react-router-dom";

interface PageMeta {
	title: string;
	description?: string;
	/**
	 * When true, neither the shared desktop `AppLayout` title block
	 * nor the `MobileHeader` heading are rendered on this route — but
	 * `title` is still used for `document.title`. The page is then
	 * expected to provide its own top-level heading. Used by the home
	 * page, which shows a personalised greeting instead of a static
	 * "Mee Admin" title.
	 */
	hideTitle?: boolean;
}

const pageMetaMap: Record<string, PageMeta> = {
	"/": { title: "Mee Admin", hideTitle: true },
	"/observations/submissions": {
		title: "Submissions",
		description:
			"View and manage observation form submissions across your organisation."
	},
	"/observations/forms": {
		title: "Forms",
		description:
			"Create, edit and manage observation forms that teams use to record assessments."
	},
	"/observations/reports": {
		title: "Reports",
		description:
			"View reporting and analytics across areas, regions, stores and individuals."
	},
	"/vendor-sponsorship/homepage-carousel": {
		title: "Carousel",
		description:
			"Manage the rotating banner carousel displayed on the app homepage."
	},
	"/vendor-sponsorship/full-screen-takeover": {
		title: "Takeover",
		description:
			"Manage full screen takeover banners that display when users open the app."
	},
	"/vendor-sponsorship/promotion": {
		title: "Promotions",
		description: "Manage promotional banners displayed on the app homepage."
	},
	"/vendor-sponsorship/app-branding": {
		title: "Branding",
		description:
			"Change the core background colour of the app, largely affecting the homepage."
	},
	"/app/homepage/quick-links": {
		title: "Quick Links",
		description: "Manage quick link tiles displayed on the app homepage."
	},
	"/app/homepage/key-shouts": {
		title: "Key Shouts",
		description: "Manage key shout tiles displayed on the app homepage."
	},
	"/app/homepage/ordering": {
		title: "Ordering",
		description:
			"Control which sections appear on the app homepage and their display order."
	},
	"/app/homepage/section-titles": {
		title: "Section Titles",
		description:
			"Manage the display titles and visibility of homepage sections."
	},
	"/app/events": {
		title: "Events",
		description: "Create and manage events displayed in the app."
	},
	"/social-feed/hashtags": {
		title: "Hashtags",
		description: "Create and manage hashtags for the social feed."
	},
	"/social-feed/favourite-feeds": {
		title: "Favourites",
		description:
			"Control which hashtags appear in the favourites feed bar and their display order."
	},
	"/social-feed": {
		title: "Social Feed",
		description: "View the social feed."
	},
	"/analytics": {
		title: "Analytics",
		description: "App usage, content performance, and adoption metrics."
	},
	"/announcements": {
		title: "Announcements",
		description:
			"Create and manage announcements sent to app users via push notification."
	},
	"/app/navigation": {
		title: "Navigation",
		description: "Manage the mobile app burger menu structure."
	},
	"/users/people": {
		title: "People",
		description: "View and manage all users across the organisation."
	},
	"/users/groups": {
		title: "Groups",
		description:
			"Create and manage groups for permissions and content targeting."
	},
	"/users/locations": {
		title: "Locations",
		description:
			"Manage the organisational hierarchy of regions, areas, and stores."
	},
	"/users/permissions": {
		title: "Permissions",
		description:
			"Create and manage permission groups that control what users can access."
	}
};

function matchPageMeta(pathname: string): PageMeta {
	// Exact match first
	if (pageMetaMap[pathname]) return pageMetaMap[pathname];

	// Try prefix match (for nested routes like /observations/submissions/123)
	const match = Object.entries(pageMetaMap)
		.filter(([path]) => path !== "/")
		.sort(([a], [b]) => b.length - a.length)
		.find(([path]) => pathname.startsWith(path));

	return match ? match[1] : { title: "Mee" };
}

/**
 * Single source of truth for route metadata. Resolves once per render
 * — prefer this over the individual `usePage*` hooks when reading
 * more than one field, so route lookup and `useLocation()` only run
 * once per consumer.
 */
export function usePageMeta(): PageMeta {
	const { pathname } = useLocation();
	return matchPageMeta(pathname);
}

export function usePageTitle(): string {
	return usePageMeta().title;
}

export function usePageDescription(): string | undefined {
	return usePageMeta().description;
}

export function useHidePageTitle(): boolean {
	return usePageMeta().hideTitle ?? false;
}
